/**
 * PaymentBadges — شعارات الدفع المفعّلة بالأيقونات الرسميّة
 * (نفس أيقونات صفحة الدفع في Shopify، بمقاس 38×24 موحَّد).
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/contexts/LocaleContext";
import visa from "@/assets/pay-visa.svg.asset.json";
import master from "@/assets/pay-master.svg.asset.json";
import amex from "@/assets/pay-american_express.svg.asset.json";
import applePay from "@/assets/pay-apple_pay.svg.asset.json";
import googlePay from "@/assets/pay-google_pay.svg.asset.json";
import shopPay from "@/assets/pay-shopify_pay.svg.asset.json";
import tabby from "@/assets/pay-tabby.svg.asset.json";
import tamara from "@/assets/pay-tamara.svg.asset.json";

interface PaymentBadgesProps {
  className?: string;
  compact?: boolean;
}

const BADGES: Array<{ key: string; label: string; src?: string }> = [
  { key: "visa", label: "Visa", src: visa.url },
  { key: "mastercard", label: "Mastercard", src: master.url },
  { key: "amex", label: "American Express", src: amex.url },
  { key: "apple_pay", label: "Apple Pay", src: applePay.url },
  { key: "google_pay", label: "Google Pay", src: googlePay.url },
  { key: "shop_pay", label: "Shop Pay", src: shopPay.url },
  { key: "tabby", label: "Tabby", src: tabby.url },
  { key: "tamara", label: "Tamara", src: tamara.url },
  { key: "cod", label: "Cash on Delivery" },
];

/** Tabby وTamara متاحان داخل الإمارات فقط — يُخفيان لأيّ سوق آخر. */
const UAE_ONLY_BADGES = new Set(["tabby", "tamara"]);

export function visiblePaymentBadges(marketId: string) {
  if (marketId === "AE") return BADGES;
  return BADGES.filter((b) => !UAE_ONLY_BADGES.has(b.key));
}

export function PaymentBadges({ className, compact = false }: PaymentBadgesProps) {
  const { locale, marketId } = useLocale();
  const label = locale === "ar" ? "طرق الدفع المقبولة" : "Accepted payment methods";
  const size = compact ? "h-5 w-8" : "h-6 w-[38px]";
  const badges = visiblePaymentBadges(marketId);

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <ul dir="ltr" className="flex flex-wrap items-center justify-center gap-2" aria-label={label}>
        {badges.map((b) => (
          <li key={b.key} title={b.label} className={cn("flex items-center justify-center", size)}>
            {b.src ? (
              <img src={b.src} alt={b.label} className="h-full w-full" loading="lazy" />
            ) : (
              <span className="flex h-full w-full items-center justify-center rounded-[3px] border border-border bg-card text-[8px] font-bold tracking-wide text-foreground">
                COD
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
